import axios from 'axios';
import { api } from './api';
import { ensureDeviceFp } from './deviceFingerprint';
import { getClientId } from './clientId';
import type { EntitlementItem } from '../types/api';

export interface UserGroup {
  id: number;
  code: string;
  name: string;
  level: number;
  monthlyPrice: number;
  yearlyPrice: number;
  taobaoLink: string | null;
  dailyDownloadLimit: number | null;
  dailyUnlockLimit: number | null;
  description: string | null;
  sortOrder: number;
}

export interface RedeemAlreadyOwnedInfo {
  usedAt: string;
  datasetCode?: string;
  datasetName?: string;
  grantedAt?: string;
  entitlementStatus?: string;
  premiumExpireAt?: string | null;
}

export interface RedeemUserResponse {
  ok: boolean;
  mode: 'claimed' | 'newly_redeemed' | 'already_owned';
  code: string;
  type: string;
  entitlementId: number | null;
  remainingDownloads: number | null;
  expiresAt: string | null;
  /** mode='already_owned' 时才有：本人此前兑换的详情 */
  alreadyOwned?: RedeemAlreadyOwnedInfo;
}

export interface AnonymousRedeemResponse {
  ok: boolean;
  anonymousToken: string;
  // CR-8 阶段 1：lookup 双因子凭证。仅在兑换响应中返回一次，丢失后无法用 lookup 恢复
  lookupSecret?: string;
  code: string;
  dataset: { id: number; code: string; name: string; entityLevel: string | null; size: string | null };
  remainingDownloads: number;
  expiresAt: string | null;
}

export interface AnonymousLookupResponse {
  ok: boolean;
  anonymousToken: string;
  code: string;
  dataset: { id: number; code: string; name: string; entityLevel: string | null; size: string | null };
  remainingDownloads: number;
  expiresAt: string | null;
}

export interface ClaimAnonymousResult {
  token: string;
  status: 'claimed' | 'alreadyOwned' | 'invalid';
  code?: string;
  datasetCode?: string;
  datasetName?: string;
}
export interface ClaimAnonymousResponse {
  results: ClaimAnonymousResult[];
  claimedCount: number;
  alreadyOwnedCount: number;
  invalidCount: number;
}

/** 匿名兑换专用 axios 实例：不附带用户 access token，避免被识别成已登录请求。 */
export const anonymousApi = axios.create({
  baseURL: '/api',
  withCredentials: false,
  timeout: 30_000,
});

// 反盗版：匿名链路（lookup / 下载票据）同样注入设备指纹头。
anonymousApi.interceptors.request.use(async (config) => {
  const fp = await ensureDeviceFp();
  if (fp) {
    config.headers = config.headers ?? {};
    (config.headers as Record<string, string>)['X-Device-FP'] = fp;
  }
  const cid = getClientId();
  if (cid) {
    config.headers = config.headers ?? {};
    (config.headers as Record<string, string>)['X-Client-Id'] = cid;
  }
  return config;
});

export const redemptionApi = {
  redeem: (code: string) => api.post<RedeemUserResponse>('/redemptions/redeem', { code }),
  // 登录态：把本浏览器匿名 token 批量认领到账号（走带 token 的 api 实例）
  claimAnonymous: (tokens: string[]) =>
    api.post<ClaimAnonymousResponse>('/redemptions/claim-anonymous', { tokens }),
  anonymousRedeem: (code: string) =>
    anonymousApi.post<AnonymousRedeemResponse>('/anonymous/redemptions', { code }),
  // CR-8 阶段 1：lookup 双因子。secret 是兑换时返回的 12 位 base32
  anonymousLookup: (code: string, lookupSecret: string) =>
    anonymousApi.post<AnonymousLookupResponse>('/anonymous/redemptions/lookup', {
      code,
      lookupSecret,
    }),
  anonymousDownloadUrl: (datasetCode: string) =>
    `/api/anonymous/downloads/${encodeURIComponent(datasetCode)}`,
};

export const subscriptionApi = {
  listGroups: () => api.get<{ items: UserGroup[] }>('/user-groups'),
};

export type { EntitlementItem };
